"use client"

import { ArrowDownRight, ArrowUpRight, Bot, Briefcase, Clock } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import type { BackendPortfolio } from "@/lib/agent-force"
import { parseAppUtcDate } from "@/lib/format-datetime"

const AGENT_MT5_MAGIC = new Set([11001, 11002, 11003])

interface OpenRow {
  id: string
  pair: string
  direction: "BUY" | "SELL"
  lot: string
  priceLine: string
  slTpLine: string
  profit: string
  profitPositive: boolean | null
  isAgent: boolean
  openedLabel: string
  durationLabel: string
}

function symbolToPair(symbol: string): string {
  const s = symbol.replace(/[^A-Za-z]/g, "").toUpperCase()
  if (s.length === 6) return `${s.slice(0, 3)}/${s.slice(3)}`
  return symbol
}

function priceDecimalsForSymbol(symbol: string): number {
  const s = symbol.toUpperCase()
  if (s.includes("JPY") || s.includes("XAU")) return 2
  return 5
}

function formatPrice(symbol: string, value: unknown): string {
  const n = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return "—"
  const d = Math.min(8, Math.max(2, priceDecimalsForSymbol(symbol)))
  return n.toFixed(d)
}

function formatMoney(value: unknown): { text: string; positive: boolean | null } {
  const n = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(n)) return { text: "—", positive: null }
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Math.abs(n))
  const signed = n > 0 ? `+${formatted}` : n < 0 ? `-${formatted}` : formatted
  return { text: signed, positive: n > 0 ? true : n < 0 ? false : null }
}

function formatDuration(openedAt: unknown): string {
  const ms = parseAppUtcDate(String(openedAt ?? ""))?.getTime()
  if (ms == null || !Number.isFinite(ms)) return "—"
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`
}

function formatOpened(openedAt: unknown): string {
  const dt = parseAppUtcDate(String(openedAt ?? ""))
  if (!dt) return "—"
  return dt.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
}

function normalizeOpenRows(portfolio: BackendPortfolio | null | undefined): OpenRow[] {
  const raw = portfolio?.open_positions ?? portfolio?.positions ?? []
  if (!Array.isArray(raw)) return []

  return raw
    .filter((p): p is Record<string, unknown> => p != null && typeof p === "object")
    .map((pos, idx) => {
      const symbol = String(pos.symbol ?? "N/A")
      const directionRaw = String(pos.direction ?? "buy").toUpperCase()
      const direction: "BUY" | "SELL" = directionRaw === "SELL" ? "SELL" : "BUY"
      const magic = Number(pos.magic)
      const openedAt = pos.opened_at_utc ?? pos.opened_at_local ?? pos.opened_at
      const sl = formatPrice(symbol, pos.sl)
      const tp = formatPrice(symbol, pos.tp)
      const profit = formatMoney(pos.profit)
      const ticket = pos.ticket != null ? String(pos.ticket) : `P${idx}`

      return {
        id: `pos-${ticket}`,
        pair: symbolToPair(symbol),
        direction,
        lot: `${Number(pos.lot ?? 0).toFixed(2)} lot`,
        priceLine: `${formatPrice(symbol, pos.price_open)} → ${formatPrice(symbol, pos.price_current)}`,
        slTpLine: `SL ${sl} · TP ${tp}`,
        profit: profit.text,
        profitPositive: profit.positive,
        isAgent: Number.isFinite(magic) && AGENT_MT5_MAGIC.has(magic),
        openedLabel: formatOpened(openedAt),
        durationLabel: formatDuration(openedAt),
      }
    })
}

function totalFloatingPnl(portfolio: BackendPortfolio | null | undefined): number | null {
  const raw = portfolio?.open_positions ?? portfolio?.positions ?? []
  if (!Array.isArray(raw) || raw.length === 0) return null
  let sum = 0
  let any = false
  for (const p of raw) {
    if (p == null || typeof p !== "object") continue
    const n = Number((p as Record<string, unknown>).profit)
    if (Number.isFinite(n)) {
      sum += n
      any = true
    }
  }
  return any ? sum : null
}

export function LiveOpenBook({
  portfolio,
  loading,
}: {
  portfolio?: BackendPortfolio | null
  loading?: boolean
}) {
  const rows = normalizeOpenRows(portfolio)
  const floatTotal = totalFloatingPnl(portfolio)
  const floatBadge =
    floatTotal == null
      ? "—"
      : formatMoney(floatTotal).text
  const asOf =
    portfolio?.as_of_utc ?? portfolio?.as_of_local ?? portfolio?.as_of ?? null
  const asOfLabel = asOf
    ? new Date(asOf).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : null

  return (
    <Card className="min-w-0 w-full bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary shrink-0" />
          Live Open Book
        </CardTitle>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className="font-mono text-xs">
            {rows.length} open
          </Badge>
          <Badge
            variant="outline"
            className={`font-mono text-xs ${
              floatTotal != null && floatTotal > 0
                ? "text-positive border-positive/40"
                : floatTotal != null && floatTotal < 0
                  ? "text-negative border-negative/40"
                  : ""
            }`}
          >
            Float {floatBadge}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {asOfLabel && (
          <p className="text-[11px] text-muted-foreground mb-2">
            MT5 snapshot · {asOfLabel}
            {portfolio?.stale ? " · stale fallback" : ""}
          </p>
        )}
        {loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading live positions…</p>
        )}
        {!loading && rows.length === 0 && (
          <div className="rounded-md border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
            No open MT5 positions. When the agents enter a trade it will appear here with live floating P/L, SL/TP,
            and agent vs manual tagging.
          </div>
        )}
        <ScrollArea className="h-[300px]">
          <div className="space-y-2 pr-2">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex items-start justify-between gap-3 p-2.5 rounded-md bg-secondary/30 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className={`shrink-0 p-1.5 rounded-md ${row.direction === "BUY" ? "bg-positive/10" : "bg-negative/10"}`}
                  >
                    {row.direction === "BUY" ? (
                      <ArrowUpRight className="h-4 w-4 text-positive" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 text-negative" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-sm">{row.pair}</span>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 ${row.direction === "BUY" ? "text-positive border-positive/30" : "text-negative border-negative/30"}`}
                      >
                        {row.direction}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-1.5 py-0 gap-1 ${row.isAgent ? "text-primary border-primary/30" : ""}`}
                      >
                        {row.isAgent ? (
                          <>
                            <Bot className="h-3 w-3" />
                            Agent
                          </>
                        ) : (
                          "Manual"
                        )}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <span className="font-mono">{row.lot}</span>
                      <span className="mx-1">·</span>
                      <span className="font-mono">{row.priceLine}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{row.slTpLine}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p
                    className={`font-mono text-sm font-semibold tabular-nums ${
                      row.profitPositive === true
                        ? "text-positive"
                        : row.profitPositive === false
                          ? "text-negative"
                          : ""
                    }`}
                  >
                    {row.profit}
                  </p>
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground justify-end mt-1">
                    <Clock className="h-3 w-3 shrink-0" />
                    <span>{row.durationLabel}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{row.openedLabel}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
